import { useEffect, useState } from "react";
import { PlusSquare, Share, Smartphone, X } from "lucide-react";
import { useData } from "../context/DataContext";

const KEY = "dither.installDismissed";
const IOS_KEY = "dither.iosInstallDismissed";
const SNOOZE = 14 * 24 * 60 * 60 * 1000;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const snoozed = () => Date.now() - Number(localStorage.getItem(KEY) || 0) < SNOOZE;

// One visit per browser session; returns total visits including this one.
function countVisit() {
  const n = Number(localStorage.getItem("dither.visits") || 0) + (sessionStorage.getItem("dither.visitCounted") ? 0 : 1);
  localStorage.setItem("dither.visits", String(n));
  sessionStorage.setItem("dither.visitCounted", "1");
  return n;
}

const card = "fixed inset-x-2 bottom-[72px] z-[60] mx-auto max-w-sm rounded-[20px] border-[3px] border-[#3b1800] bg-[#FFE14D] p-3 text-[#3b1800] shadow-[0_5px_0_#3b1800] animate-in slide-in-from-bottom-4 md:bottom-4 md:left-auto md:right-4";

function Close({ onClick }: { onClick: () => void }) {
  return <button onClick={onClick} aria-label="Dismiss" className="absolute right-2 top-2 rounded-full p-1 hover:bg-black/10" data-testid="install-prompt-dismiss"><X className="h-4 w-4" strokeWidth={3} /></button>;
}

function Step({ n, icon, children }: { n: number; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <li className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 text-xs font-bold" data-testid={`install-step-${n}`}>
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#3b1800] font-comic text-[11px] text-white">{n}</span>
      <span className="flex flex-wrap items-center gap-1">{children}{icon}</span>
    </li>
  );
}

function IosBanner() {
  const [show, setShow] = useState(() => !isStandalone() && !localStorage.getItem(IOS_KEY) && countVisit() >= 2);
  if (!show) return null;
  const dismiss = () => { localStorage.setItem(IOS_KEY, "1"); setShow(false); };
  return (
    <div className={card} data-testid="install-prompt">
      <Close onClick={dismiss} />
      <div className="flex items-center gap-3 pr-5">
        <img src="/apple-touch-icon-180.png" alt="Dither app icon" className="h-12 w-12 shrink-0 rounded-xl border-2 border-[#3b1800]" />
        <p className="ua-title text-lg leading-tight">Add Dither to your home screen for quick decisions anytime</p>
      </div>
      <ol className="mt-2 space-y-1.5" data-testid="install-prompt-ios-tip">
        <Step n={1} icon={<Share className="h-4 w-4 text-[#007AFF]" strokeWidth={2.5} />}>Tap the <strong>Share</strong> button</Step>
        <Step n={2} icon={<PlusSquare className="h-4 w-4" strokeWidth={2.5} />}>Choose <strong>“Add to Home Screen”</strong></Step>
      </ol>
    </div>
  );
}

function ChromePrompt() {
  const { history } = useData();
  const [deferred, setDeferred] = useState<any>(null);
  const [hidden, setHidden] = useState(() => isStandalone() || snoozed());

  useEffect(() => {
    const onPrompt = (e: Event) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setHidden(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (hidden || history.length < 2 || !deferred) return null;

  const dismiss = () => { localStorage.setItem(KEY, String(Date.now())); setHidden(true); };
  const install = async () => {
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    setDeferred(null);
    if (outcome !== "accepted") dismiss();
  };

  return (
    <div className={card} data-testid="install-prompt">
      <Close onClick={dismiss} />
      <div className="flex items-center gap-3 pr-5">
        <img src="/icon-192.png" alt="Dither app icon" className="h-12 w-12 shrink-0 rounded-xl border-2 border-[#3b1800]" />
        <div>
          <p className="ua-title text-xl leading-none">Add DITHER to your home screen</p>
          <p className="mt-0.5 text-xs font-bold">One tap to decide — works like an app.</p>
        </div>
      </div>
      <button onClick={install} className="ua-btn mt-2 flex w-full items-center justify-center gap-2 rounded-full border-[3px] border-[#3b1800] bg-[#FF5D1F] py-2 text-white transition-transform active:translate-y-0.5" data-testid="install-prompt-button">
        <Smartphone className="h-4 w-4" strokeWidth={3} /> Install DITHER
      </button>
    </div>
  );
}

export function InstallPrompt() {
  return isIos() ? <IosBanner /> : <ChromePrompt />;
}
